import React from 'react';
import {
    ButtonGroup,
    CardBody,
    CardTitle,
    CardSubtitle,
    CardFooter,
    UncontrolledTooltip,
} from 'reactstrap';
import cls from 'classnames';
import {
    faFlag, faGripVertical, faTruck, faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { weekdayLetter } from '@s20/i18n';
import { Weekdays } from '../Common';
import { INTERPUNCT } from '../common/util';
import LongTermStatusIndicator from './LongTermStatusIndicator';

export default class EquipmentCard extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            clientHeight: 0,
        };
    }

    componentDidMount() {
        const div = document.getElementById(`equipmentCard-${this.props.equipment.id}`);

        this.setState({ clientHeight: (div?.getBoundingClientRect().height ?? 0) }); // eslint-disable-line
    }

    setRef = (ref) => {
    // keep a reference to the dom ref as an instance property
        this.ref = ref;
        // give the dom ref to @hello-pangea/dnd
        this.props.innerRef(ref);
    };

    render() {
        const {
            equipment, provided, allJobs, weekdays,
            hiddenEquipmentIds, dragging, longTermStatuses, equipmentLongTermStatuses,
            onClick, selected, compact, selectedWeekRangeDays,
        } = {
            ...this.props,
        };

        if (hiddenEquipmentIds.includes(equipment.id)) {
            return (
                <div
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    ref={this.setRef}
                />
            );
        }

        const Component = dragging ? 'div' : 'dd';
        const PlaceholderComponent = dragging ? 'dd' : 'div';

        return (
            <>
                <Component
                    id={`equipmentCard-${equipment.id}`}
                    className={cls('card equipmentCard', {
                        minimal: !!this.props.minimal,
                        dragging,
                        selected,
                    })}
                    onClick={onClick}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    ref={this.setRef}
                >
                    <div className="staff-card-main">
                        {!compact && (
                            <div className="dragIndicator">
                                <FontAwesomeIcon icon={faGripVertical} />
                            </div>
                        )}

                        <CardBody>
                            <CardTitle className="card-title">
                                <span className="staff-info">
                                    {equipment.description}
                                </span>

                                <div className="status-icons">
                                    {Boolean(equipment.equipmentAssignedName) && (
                                        <>
                                            <FontAwesomeIcon
                                                id={`equipmentAssignedTruck-${equipment.id}`}
                                                icon={faTruck}
                                                className="mr-1"
                                            />

                                            <UncontrolledTooltip target={`equipmentAssignedTruck-${equipment.id}`}>
                                                {`Assigned to ${equipment.equipmentAssignedTypeName} ${INTERPUNCT} ${equipment.equipmentAssignedName}`}
                                            </UncontrolledTooltip>
                                        </>
                                    )}

                                    {Boolean(equipment.employeeAssignedName) && (
                                        <>
                                            <FontAwesomeIcon
                                                id={`equipmentAssignedEmployee-${equipment.id}`}
                                                icon={faUser}
                                                className="mr-1"
                                            />

                                            <UncontrolledTooltip target={`equipmentAssignedEmployee-${equipment.id}`}>
                                                {`Assigned to ${equipment.employeeAssignedName}`}
                                            </UncontrolledTooltip>
                                        </>
                                    )}

                                    <LongTermStatusIndicator
                                        staff={equipment}
                                        longTermStatuses={longTermStatuses}
                                        employeeLongTermStatuses={equipmentLongTermStatuses}
                                        selectedWeekRangeDays={selectedWeekRangeDays}
                                    />
                                </div>
                            </CardTitle>

                            <CardSubtitle>
                                <label>{equipment.equipmentType.description}</label>
                            </CardSubtitle>

                            <CardFooter>
                                <ButtonGroup
                                    className=""
                                    size="sm"
                                >
                                    {weekdays.map((dayDate) => {
                                        const day = Weekdays.find(
                                            (x) => x.id === dayDate.getDay(),
                                        );
                                        return (
                                            <span
                                                key={day.id}
                                                className={cls(
                                                    'btn btn-sm btn-outline-secondary scheduledDayIndicator',
                                                    {
                                                        active: !!allJobs.find(
                                                            (j) => !!j.assignments[
                                                                day.id
                                                            ].equipment.find(
                                                                (e) => !!e.assignment
                                                                    && e.assignment
                                                                        .id
                                                                        === equipment.id,
                                                            ),
                                                        ),
                                                    },
                                                )}
                                            >
                                                {weekdayLetter(day.id)}
                                            </span>
                                        );
                                    })}
                                </ButtonGroup>
                            </CardFooter>

                            {equipment.mountedEquipment?.length > 0 && (
                                <div className="mt-2 d-flex align-items-center flex-wrap">
                                    {equipment.mountedEquipment.map((mounted) => (
                                        <div
                                            key={mounted.id}
                                            id={`mountedEquipment-${equipment.id}-${mounted.id}`}
                                            className="cursor-pointer mr-2"
                                        >
                                            <FontAwesomeIcon icon={mounted.isVehicle ? faTruck : faFlag} />

                                            <UncontrolledTooltip target={`mountedEquipment-${equipment.id}-${mounted.id}`}>
                                                {`${mounted.equipmentTypeName} ${INTERPUNCT} ${mounted.description}`}
                                            </UncontrolledTooltip>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardBody>
                    </div>
                </Component>

                {!compact && (
                    <PlaceholderComponent
                        className="card equipmentCard placeholder"
                        style={{ height: this.state.clientHeight }}
                    />
                )}
            </>
        );
    }
}
